import { useCallback, useEffect, useState } from "react";
import { Loader2, Plus, RefreshCw, Repeat2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { getOutboundCallerLines, type OutboundCallerLineRow } from "@/lib/twilio-phone-dashboard-api";
import { PhoneNumberFlowModal } from "@/components/phone-number-flow-modal";
import { cn } from "@/utils";

function formatUpdatedAt(v: unknown): string {
  if (v == null) return "—";
  if (typeof v === "string") {
    const s = v.replace(/\.\d{3}Z?$/, "Z").replace("T", " ");
    return s.length > 19 ? s.slice(0, 19) : s;
  }
  if (typeof v === "object" && v !== null && "seconds" in (v as { seconds?: number })) {
    const sec = (v as { seconds: number }).seconds;
    if (typeof sec === "number") {
      return new Date(sec * 1000).toISOString().slice(0, 19).replace("T", " ");
    }
  }
  return "—";
}

function trunc(s: string | undefined | null, n: number): string {
  if (!s) return "—";
  return s.length <= n ? s : `${s.slice(0, n)}…`;
}

export function DashboardPhoneNumbersPanel() {
  const [lines, setLines] = useState<OutboundCallerLineRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<"replace" | "add">("add");
  const [modalLine, setModalLine] = useState<OutboundCallerLineRow | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getOutboundCallerLines(300);
      setLines(res.lines ?? []);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load lines");
      setLines([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const openReplace = (line: OutboundCallerLineRow) => {
    setModalLine(line);
    setModalMode("replace");
    setModalOpen(true);
  };

  const openAdd = () => {
    setModalLine(null);
    setModalMode("add");
    setModalOpen(true);
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">
          Caller ID lines by provider region (state and optional ZIP). When a case is dialed, the
          system picks the line that best matches that case&apos;s provider location.
        </p>
        <div className="flex flex-wrap gap-2">
          <Button type="button" variant="outline" size="sm" onClick={() => void load()} disabled={loading}>
            <RefreshCw className={cn("mr-2 h-4 w-4", loading && "animate-spin")} aria-hidden />
            Refresh
          </Button>
          <Button type="button" size="sm" onClick={openAdd}>
            <Plus className="mr-2 h-4 w-4" aria-hidden />
            Add new number
          </Button>
        </div>
      </div>

      {error ? (
        <p className="text-sm text-destructive" role="alert">
          {error}
        </p>
      ) : null}

      {loading ? (
        <div
          className="flex items-center gap-2 rounded-lg border border-dashed border-border px-4 py-8 text-sm text-muted-foreground"
          role="status"
        >
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
          Loading numbers…
        </div>
      ) : lines.length === 0 ? (
        <div className="rounded-lg border border-border bg-muted/20 px-4 py-10 text-center text-sm text-muted-foreground">
          No outbound lines yet. Use <strong className="text-foreground">Add new number</strong> to
          search Twilio, pick a number, and purchase it.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-border">
          <table className="w-full min-w-[900px] text-left text-sm">
            <thead className="border-b border-border bg-muted/40">
              <tr>
                <th className="px-3 py-2.5 font-medium">Document</th>
                <th className="px-3 py-2.5 font-medium">State</th>
                <th className="px-3 py-2.5 font-medium">ZIP</th>
                <th className="px-3 py-2.5 font-medium">Phone</th>
                <th className="px-3 py-2.5 font-medium">Vapi ID</th>
                <th className="px-3 py-2.5 font-medium">Twilio SID</th>
                <th className="px-3 py-2.5 font-medium">Updated</th>
                <th className="px-3 py-2.5 font-medium w-[1%]"> </th>
              </tr>
            </thead>
            <tbody>
              {lines.map((line) => (
                <tr key={line.document_id} className="border-b border-border/80 last:border-0">
                  <td className="px-3 py-2.5 font-mono text-xs">{line.document_id}</td>
                  <td className="px-3 py-2.5">{String(line.provider_state ?? "—")}</td>
                  <td className="px-3 py-2.5">
                    {line.provider_postal_code != null && String(line.provider_postal_code) !== ""
                      ? String(line.provider_postal_code)
                      : "—"}
                  </td>
                  <td className="px-3 py-2.5 font-mono text-xs">
                    {String(line.phone_number_e164 ?? "—")}
                  </td>
                  <td className="px-3 py-2.5 font-mono text-xs text-muted-foreground">
                    {trunc(line.vapi_phone_number_id as string | undefined, 14)}
                  </td>
                  <td className="px-3 py-2.5 font-mono text-xs text-muted-foreground">
                    {trunc(line.twilio_incoming_phone_sid as string | undefined, 12)}
                  </td>
                  <td className="px-3 py-2.5 text-muted-foreground">
                    {formatUpdatedAt(line.updated_at)}
                  </td>
                  <td className="px-3 py-2.5 text-right">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => openReplace(line)}
                    >
                      <Repeat2 className="mr-1.5 h-3.5 w-3.5" aria-hidden />
                      Replace
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <PhoneNumberFlowModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        mode={modalMode}
        existingLine={modalMode === "replace" ? modalLine : null}
        onCompleted={() => void load()}
      />
    </div>
  );
}